"use client";
import React from "react";
import { VeBox, VeTypography, useMediaQuery } from "@packages/ui/mui";
import { SubmitHandler, useForm, z, zodResolver } from "@packages/ui/libs";
import { AuthForm, VEAlert, VeButton } from "@packages/ui/components";
import styles from "./signIn.module.scss";
import { VeInput } from "@packages/ui/components";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { signIn } from "next-auth/react";
import { VE_ICON_GREY, VE_ICON_PURPLE } from "@public/index";
import axios from "axios";
import AuthLinkDesc from "@components/AuthLinkDesc/authLinkDesc";
import { getExpiryTime } from "@libs/helper";
import Image from "next/image";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";

const validationSchema = z.object({
  email: z
    .string()
    .email("Please enter a valid email.")
    .min(1, "Please enter a valid email."),
  password: z.string().min(1, "Please enter a valid password."),
});

type ValidationSchema = z.infer<typeof validationSchema>;

const page = () => {
  const search = useSearchParams();
  const prefillEmail = search.get("email");

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ValidationSchema>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
    defaultValues: {
      email: prefillEmail ? prefillEmail : "",
    },
  });

  console.log(errors);

  const queryClient = useQueryClient();
  const router = useRouter();
  const { mutate, error, isPending } = useMutation({
    mutationFn: async ({
      data,
      headersReq,
      endPoint,
    }: {
      data: any;
      headersReq: any;
      endPoint: any;
    }) => {
      const response = await axios.post("/api/signin", {
        data,
        headersReq,
        endPoint,
      });
      return response.data;
    },
    mutationKey: ["login"],
    onSuccess: async (data: any, variables) => {

      if (data.error)
        throw new Error(data.error?.heading, { cause: data?.error?.message });

      // Check if userType is array and contains only SUPER_ADMIN/ADMIN
      const userTypes = Array.isArray(data?.userTypes)
        ? data.userTypes
        : [data?.userTypes];
      const hasOnlyAdminTypes =
        userTypes.length > 0 &&
        userTypes.every(
          (type: string) => type === "SUPER_ADMIN" || type === "ADMIN",
        ) &&
        !userTypes.includes("USER") &&
        !userTypes.includes("PRIMARY_USER");

      // if (hasOnlyAdminTypes && !hasSellerInternalRole) {
      //   throw new Error("Invalid Credentials", {
      //     cause:
      //       "The email or password entered is incorrect. Please try again.",
      //   });
      // }
      if (data?.isMFARequired && data?.MFAStatus === "NONE") {
        queryClient.setQueryData(["login-setup"], {
          ...data,
          ...variables?.data,
          skip: true,
        });
        router.push("/two-factor-auth");
        return;
      }
      if (data.token) {
        const ttl = Number(data?.refreshTokenTtl);
        const refreshTokenExpiryTime = getExpiryTime(ttl);
        await signIn("credentials", { ...data, refreshTokenExpiryTime });
        // queryClient.setQueryData(["login-setup"],  {...data,...variables});
        // router.push("/two-factor-auth");
        return;
      }
      if (data.challengeName === "SMS_MFA") {
        queryClient.setQueryData(["login"], data);
        router.push("/sms-verification");
        return;
      }
      if (data.challengeName === "SOFTWARE_TOKEN_MFA") {
        queryClient.setQueryData(["login"], data);
        router.push("/authenticator-verification");
        return;
      }
      if (data.challengeName === "NEW_PASSWORD_REQUIRED") {
        queryClient.setQueryData(["login"], {
          ...data,
          ...variables?.data,
        });
        router.push("/create-new-password");
        return;
      }
    },
  });

  const onSubmit: SubmitHandler<ValidationSchema> = (data) => {
    mutate({
      data: { ...data, email: data?.email?.toLowerCase() },
      headersReq: { "user-agent": window.navigator.userAgent },
      endPoint: "/auth/sign-in",
    });
  };

  return (
    <VeBox sx={{ maxWidth: "400px" }}>
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <AuthForm
          border={true}
          icon={
            <VeBox>
              <Image src={VE_ICON_PURPLE} alt="VE_LOGO" />
            </VeBox>
          }
          heading="Sign In"
          UnderButton={
            <VeBox marginTop={"24px"}>
              <VeTypography className="link" variant="label2StrongBrand">
                <Link href={"/forgot-password"}>Forgot Password?</Link>
              </VeTypography>
            </VeBox>
          }
          Alert={
            error && (
              <VeBox width="100%" marginTop="16px">
                <VEAlert
                  heading={error?.message}
                  alertType="error"
                  paragraph={(error?.cause as string) || ""}
                />
              </VeBox>
            )
          }
        >
          <VeBox
            width={"100%"}
            display="flex"
            flexDirection="column"
            gap="16px"
          >
            <VeInput
              label="Email"
              type="email"
              register={register}
              name="email"
              error={!!errors.email}
              errorText={errors.email?.message || ""}
            />
            <VeInput
              label="Password"
              error={!!errors.password}
              errorText={errors.password?.message || ""}
              register={register}
              name="password"
              type="password"
            />
          </VeBox>
          <VeButton
            styles={{ marginTop: "32px" }}
            type="submit"
            size="large"
            fullWidth
            disabled={!isValid}
            buttonVariant="contained"
            color="primary"
            loading={isPending}
          >
            Sign In
          </VeButton>
        </AuthForm>
        <AuthLinkDesc />
      </form>
    </VeBox>
  );
};

export default page;
